import cls from "classnames";
import {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ComponentType,
  MouseEvent,
  ReactNode,
} from "react";
import { Link, LinkProps } from "react-router-dom";

import { omit } from "utils/object";
import type { WithDiscriminatedProps } from "utils/types";

import Spinner from "components/ui/Spinner";

import styles from "./index.module.scss";

type CommonProps = {
  children?: ReactNode;
  /** How much weight the action carries. */
  variant?: "primary" | "secondary" | "plain";
  /** What the action means — a separate axis from its weight. */
  tone?: "neutral" | "danger";
  size?: "extra-small" | "small" | "medium" | "large" | "extra-large";
  loading?: boolean;
  selected?: boolean;
  disabled?: boolean;
  Icon?: ComponentType<{ className?: string; htmlColor?: string }>;
  iconColor?: string;
  className?: string;
  // e2e markers (HTMLAttributes has no data-* index signature for custom components).
  "data-cy"?: string;
  "data-testid"?: string;
};

type ButtonAttributes = ButtonHTMLAttributes<HTMLButtonElement>;
type AnchorAttributes = AnchorHTMLAttributes<HTMLAnchorElement>;

type Props =
  | WithDiscriminatedProps<CommonProps, { as?: "button" }, ButtonAttributes>
  | WithDiscriminatedProps<CommonProps, { as: "a"; href: string }, AnchorAttributes>
  | WithDiscriminatedProps<CommonProps, { as: "link" }, LinkProps>;

// Peeled per branch rather than destructured in the header: a union's rest
// element does not narrow with the discriminant.
const KIT_PROPS = [
  "as",
  "children",
  "variant",
  "tone",
  "size",
  "loading",
  "selected",
  "disabled",
  "Icon",
  "iconColor",
  "className",
] as const;

// The app's button, rendering as a `<button>`, an `<a>` or a router `Link`.
export default function Button(props: Props) {
  // `disabled` does nothing on an anchor, so the two link forms have to be made
  // inert by hand: no destination, out of the tab order, and announced as
  // disabled rather than simply gone.
  const inert = props.disabled || props.loading;
  const className = buttonClassName(props);
  const content = (
    <>
      {props.loading && (
        <Spinner placement="inline" size="small" color="var(--action-spinner-fg)" />
      )}
      {props.Icon && (
        <props.Icon
          className={cls(styles.icon, !props.iconColor && styles.iconInk)}
          htmlColor={props.iconColor}
        />
      )}
      {props.children}
    </>
  );

  if (props.as === "a") {
    const { href, ...rest } = omit(props, KIT_PROPS);
    return (
      <a
        {...rest}
        className={className}
        href={inert ? undefined : href}
        aria-disabled={inert || undefined}
        tabIndex={inert ? -1 : rest.tabIndex}
        onClick={inert ? undefined : rest.onClick}
      >
        {content}
      </a>
    );
  }

  if (props.as === "link") {
    const rest = omit(props, KIT_PROPS);
    return (
      <Link
        {...rest}
        className={className}
        aria-disabled={inert || undefined}
        tabIndex={inert ? -1 : rest.tabIndex}
        onClick={inert ? cancelNavigation : rest.onClick}
      >
        {content}
      </Link>
    );
  }

  const rest = omit(props, KIT_PROPS);
  return (
    // Before the spread: three call sites need type="submit".
    <button type="button" {...rest} className={className} disabled={inert}>
      {content}
    </button>
  );
}

function cancelNavigation(event: MouseEvent) {
  event.preventDefault();
}

function buttonClassName(props: Props) {
  const { variant = "primary", tone = "neutral", size = "medium" } = props;
  return cls(
    styles.button,
    "talaera-modern-button",
    props.className,
    variant === "primary" && styles.primary,
    variant === "secondary" && styles.secondary,
    variant === "plain" && styles.plainButton,
    tone === "danger" && styles.danger,
    // Icon-only. With a label beside it the control is its variant with an icon
    // in it, and keeps that variant's geometry and hover.
    !!props.Icon && !props.children && styles.iconButton,
    props.disabled && styles.disabled,
    props.selected && styles.selected,
    props.loading && styles.loading,
    size === "extra-small" && styles.extraSmall,
    size === "small" && styles.small,
    size === "medium" && styles.medium,
    size === "large" && styles.large,
    size === "extra-large" && styles.extraLarge,
    // A block-level flex container fills its row, which a <button> resists and
    // an <a> does not.
    (props.as === "a" || props.as === "link") && styles.inline
  );
}
